"use client"

import Image from "next/image"
import { BASE_PATH } from "@/constants/app.mjs"
import { FADE_MASK_STYLE } from "@/utils/masonryMask"

export function MethodologyCard() {
  return (
    <div
      className="relative border-t border-l border-solid border-border-default rounded-tl-2xl bg-bg-default dark:bg-neutral-900"
      style={{
        maxHeight: "500px",
        overflow: "hidden",
        minHeight: "500px",
        ...FADE_MASK_STYLE,
      }}
    >
      <div className="overflow-hidden relative w-full h-full">
        <div className="w-full p-6 pr-12 pb-32">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-1">
              <Image
                src={`${BASE_PATH}/static/images/marketing/logos/aave.png`}
                alt="Aave"
                width={24}
                height={24}
                className="w-6 h-6 rounded-full object-cover"
              />
            </div>
            <h2 className="text-xl font-medium tracking-tight text-fg-default mt-2">Methodology</h2>
            <p className="text-sm text-fg-secondary">Total value locked</p>
          </div>

          {/* Section 1: Overview */}
          <section className="mb-6">
            <h3 className="text-base font-medium text-fg-default mb-3">1. Overview</h3>
            <p className="text-sm text-fg-secondary leading-relaxed">
              AAVE TVL (Total Value Locked) measures the total USD value of assets deposited in AAVE
              lending protocols across multiple blockchain networks. The metric combines both idle
              deposits (assets available for borrowing) and active loans (borrowed assets) to
              represent the complete economic value secured by the protocol. This comprehensive
              approach captures the full utilization of deposited capital, as borrowed funds remain
              part of the protocol&apos;s TVL while generating yield for depositors.
            </p>
          </section>

          {/* Section 2: Data sources */}
          <section className="mb-6">
            <h3 className="text-base font-medium text-fg-default mb-3">2. Data sources</h3>
            <p className="text-sm text-fg-secondary mb-3">
              The AAVE TVL calculation relies on several categories of blockchain data:
            </p>
            <ul className="text-sm text-fg-secondary space-y-2">
              <li className="flex gap-2">
                <span className="text-fg-secondary">•</span>
                <span>
                  <strong className="text-fg-default">Token transfer events:</strong> Parsed{" "}
                  <code className="px-1.5 py-0.5 bg-bg-surface rounded-sm text-xs font-mono">
                    Transfer
                  </code>{" "}
                  events from Ethereum, Arbitrum, Avalanche, Base, Optimism, Polygon, Gnosis, BSC,
                  ZKSync Era, Sonic, Scroll, Linea, and Plasma networks tracking movements to and
                  from aToken addresses
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-fg-secondary">•</span>
                <span>
                  <strong className="text-fg-default">Debt token events:</strong> Aave
                  protocol-specific{" "}
                  <code className="px-1.5 py-0.5 bg-bg-surface rounded-sm text-xs font-mono">
                    Mint
                  </code>{" "}
                  and{" "}
                  <code className="px-1.5 py-0.5 bg-bg-surface rounded-sm text-xs font-mono">
                    Burn
                  </code>{" "}
                  events from VToken (variable debt) and SToken (stable debt) contracts across all
                  supported networks
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-fg-secondary">•</span>
                <span>
                  <strong className="text-fg-default">Market initialization events:</strong>{" "}
                  <code className="px-1.5 py-0.5 bg-bg-surface rounded-sm text-xs font-mono">
                    ReserveInitialized
                  </code>{" "}
                  events from Aave LendingPoolConfigurator contracts that establish which tokens are
                  available for lending
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-fg-secondary">•</span>
                <span>
                  <strong className="text-fg-default">Rebasing token events:</strong> Lido protocol
                  events including{" "}
                  <code className="px-1.5 py-0.5 bg-bg-surface rounded-sm text-xs font-mono">
                    PostTotalShares
                  </code>{" "}
                  (v1) and{" "}
                  <code className="px-1.5 py-0.5 bg-bg-surface rounded-sm text-xs font-mono">
                    TokenRebased
                  </code>{" "}
                  (v2) for handling stETH rebasing mechanics
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-fg-secondary">•</span>
                <span>
                  <strong className="text-fg-default">Price feeds:</strong> End-of-day token price
                  feeds for USD conversion
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-fg-secondary">•</span>
                <span>
                  <strong className="text-fg-default">Token metadata:</strong> Token decimal
                  information and symbols for proper value calculation
                </span>
              </li>
            </ul>
          </section>

          {/* Section 3: Step-by-step calculation */}
          <section className="mb-6">
            <h3 className="text-base font-medium text-fg-default mb-3">
              3. Step-by-step calculation
            </h3>

            <div className="mb-4">
              <h4 className="text-sm font-medium text-fg-default mb-2">
                Component A: Market identification
              </h4>
              <ul className="text-sm text-fg-secondary space-y-2">
                <li className="flex gap-2">
                  <span className="text-fg-secondary">•</span>
                  <span>
                    <strong className="text-fg-default">Purpose:</strong> Identify all valid AAVE
                    lending markets across supported blockchains
                  </span>
                </li>
                <li className="flex gap-2">
                  <span className="text-fg-secondary">•</span>
                  <span>
                    <strong className="text-fg-default">Logic:</strong> Parse{" "}
                    <code className="px-1.5 py-0.5 bg-bg-surface rounded-sm text-xs font-mono">
                      ReserveInitialized
                    </code>{" "}
                    events from AAVE v2 and v3 LendingPoolConfigurator contracts to extract
                    underlying token addresses, aToken addresses, stable debt token (sToken)
                    addresses, and variable debt token (vToken) addresses. Map these to specific
                    lending pools using hardcoded pool configurator address mappings that identify
                    the pool name and main pool address for each network and version combination.
                  </span>
                </li>
                <li className="flex gap-2">
                  <span className="text-fg-secondary">•</span>
                  <span>
                    <strong className="text-fg-default">Key fields:</strong>{" "}
                    <code className="px-1.5 py-0.5 bg-bg-surface rounded-sm text-xs font-mono">
                      underlying_token_address
                    </code>
                    ,{" "}
                    <code className="px-1.5 py-0.5 bg-bg-surface rounded-sm text-xs font-mono">
                      atoken_address
                    </code>
                    ,{" "}
                    <code className="px-1.5 py-0.5 bg-bg-surface rounded-sm text-xs font-mono">
                      stoken_address
                    </code>
                    ,{" "}
                    <code className="px-1.5 py-0.5 bg-bg-surface rounded-sm text-xs font-mono">
                      vtoken_address
                    </code>
                    ,{" "}
                    <code className="px-1.5 py-0.5 bg-bg-surface rounded-sm text-xs font-mono">
                      pool_address
                    </code>
                    ,{" "}
                    <code className="px-1.5 py-0.5 bg-bg-surface rounded-sm text-xs font-mono">
                      pool_name
                    </code>
                  </span>
                </li>
              </ul>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
